// Shared AnyChart chrome for the time-series pages (WDB, Backtesting, Trading
// Strategy). Same visual language as the MTF ForecastChart: hairline axes, mono
// tick labels, horizontal grid only, dark union tooltip, no in-chart title or
// legend (the page states those in HTML). The x axis is a real date-time scale,
// so ticks land on calendar boundaries instead of every Nth category string.
import anychart from "anychart";

anychart.format.outputTimezone(0);

export const css = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
};

/** "2026-10-23" or "2026/10/23" (optionally with a time part) -> UTC ms. */
export function toTime(d) {
  if (typeof d === "number") return d;
  const m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(String(d));
  if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3]);
  const t = Date.parse(d);
  return Number.isNaN(t) ? null : t;
}

/** {date: value} or [[date, value]] -> [[ms, number]] sorted, nulls dropped. */
export function toPoints(src) {
  const rows = Array.isArray(src) ? src : Object.entries(src || {});
  return rows
    .map(([d, v]) => [toTime(d), v === null || v === "" ? null : Number(v)])
    .filter(([t, v]) => t !== null && v !== null && Number.isFinite(v))
    .sort((a, b) => a[0] - b[0]);
}

const DAY = 86400000;

/** Tick interval + label pattern that keeps roughly 6-12 labels on the axis. */
function tickPlan(spanMs) {
  const years = spanMs / (365.25 * DAY);
  if (years > 12) return { unit: "year", count: 2, minor: ["year", 1], fmt: "yyyy" };
  if (years > 5) return { unit: "year", count: 1, minor: ["month", 6], fmt: "yyyy" };
  if (years > 2) return { unit: "month", count: 6, minor: ["month", 3], fmt: "MMM yyyy" };
  if (years > 0.75) return { unit: "month", count: 3, minor: ["month", 1], fmt: "MMM yyyy" };
  if (years > 0.4) return { unit: "month", count: 1, minor: ["week", 1], fmt: "dd MMM" };
  return { unit: "week", count: 2, minor: ["week", 1], fmt: "dd MMM" };
}

export const fmtDate = (t, pattern = "dd MMM yyyy") =>
  anychart.format.dateTime(t, pattern);

export function fmtNumber(v, digits) {
  if (v === null || v === undefined || !Number.isFinite(+v)) return "–";
  const a = Math.abs(v);
  const d = digits !== undefined ? digits : a >= 1000 ? 0 : a >= 100 ? 1 : a >= 10 ? 2 : 3;
  return (+v).toLocaleString("en-US", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

/** Create a styled line chart bound to `container` (element or id). */
export function createTimeChart(container) {
  const chart = anychart.line();
  const mono = css("--f-mono", "JetBrains Mono");
  const line = css("--line", "#dbe1ea");
  const mute = css("--text-mute", "#94a0b4");

  chart.background().fill(css("--bg-1", "#ffffff"));
  chart.title(false);
  chart.legend(false);
  chart.padding([12, 16, 4, 4]);
  chart.animation(false);

  chart.xScale(anychart.scales.dateTime());
  chart.yScale().minimumGap(0.08).maximumGap(0.08);

  for (const axis of [chart.xAxis(), chart.yAxis()]) {
    axis.stroke(line);
    axis.labels().fontFamily(mono).fontSize(10.5).fontColor(mute);
  }
  chart.yAxis().ticks(false);
  chart.xAxis().ticks().enabled(true).stroke(line).length(4);
  chart.xAxis().minorTicks().enabled(true).stroke(css("--line-soft", "#eef1f5")).length(2);
  chart.xAxis().labels().padding([6, 0, 0, 0]);
  chart.xAxis().overlapMode("noOverlap");
  chart.yAxis().labels().format(function () {
    return fmtNumber(this.value);
  });

  chart.yGrid().enabled(true).stroke(css("--grid", "#e8ecf2"));
  chart.xGrid().enabled(false);

  chart.crosshair().enabled(true).yLabel(false).yStroke(null).xLabel(false);
  chart.crosshair().xStroke(css("--line-strong", "#c3ccd9"), 1, "3 3");

  const tt = chart.tooltip();
  tt.displayMode("union").useHtml(true).positionMode("point");
  tt.fontFamily(mono).fontSize(11).fontColor("#ffffff");
  tt.background().fill(css("--text", "#0d1420")).stroke(null).corners(0);
  tt.padding([6, 9, 6, 9]);
  tt.titleFormat(function () {
    return fmtDate(this.x);
  });
  tt.title().fontFamily(mono).fontSize(10.5).fontColor("#c3ccd9").fontWeight(400);
  tt.separator(false);

  chart.container(container);
  return chart;
}

/** Fit the date axis to [min, max] and pick calendar ticks for that span. */
export function setTimeDomain(chart, min, max) {
  const xs = chart.xScale();
  xs.minimum(min).maximum(max);
  const plan = tickPlan(max - min);
  xs.ticks().interval(plan.unit, plan.count);
  xs.minorTicks().interval(plan.minor[0], plan.minor[1]);
  chart.xAxis().labels().format(function () {
    return fmtDate(this.tickValue, plan.fmt);
  });
}

/** 1-2-5 step that gives about `target` intervals over `span`. */
function niceStep(span, target = 6) {
  const raw = span / target;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const r = raw / mag;
  return (r <= 1 ? 1 : r <= 2 ? 2 : r <= 5 ? 5 : 10) * mag;
}

/** Fix the value axis to round gridlines that bracket `values` with headroom. */
export function setValueDomain(chart, values) {
  const vs = values.filter((v) => Number.isFinite(v));
  if (!vs.length) return;
  let lo = Math.min(...vs);
  let hi = Math.max(...vs);
  if (lo === hi) {
    const d = Math.abs(lo) * 0.05 || 1;
    lo -= d;
    hi += d;
  }
  const pad = (hi - lo) * 0.06;
  const step = niceStep(hi - lo + 2 * pad);
  const ys = chart.yScale();
  ys.minimum(Math.floor((lo - pad) / step) * step);
  ys.maximum(Math.ceil((hi + pad) / step) * step);
  ys.ticks().interval(step);
  const digits = Math.max(0, -Math.floor(Math.log10(step)));
  chart.yAxis().labels().format(function () {
    return fmtNumber(this.value, Math.min(digits, 4));
  });
}

/** Add a line series with brand styling and a one-line tooltip row. */
export function addLine(chart, points, { name, color, width = 1.6, dash = null, digits }) {
  const s = chart.line(points);
  s.name(name);
  s.stroke({ color, thickness: width, dash: dash || undefined });
  s.hovered().stroke({ color, thickness: width + 0.6, dash: dash || undefined });
  s.hovered().markers().enabled(true).type("circle").size(3).fill(color).stroke(null);
  s.tooltip().format(function () {
    return (
      `<span style="display:inline-block;width:8px;height:2px;margin-right:6px;vertical-align:middle;background:${color}"></span>` +
      `${name}: <b>${fmtNumber(this.value, digits)}</b>`
    );
  });
  return s;
}

export function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Safe dispose: AnyChart throws if the logo loader is still in flight. */
export function disposeChart(chart) {
  if (!chart) return;
  try {
    chart.dispose();
  } catch (e) {
    /* already disposed */
  }
}
